import { useParams } from "react-router";
import { format } from "date-fns";
import _mapValues from "lodash/mapValues";
import { useFormatMsg } from "src/hooks/useFormatMsg";
import { Options } from "src/components/atoms/inputs/FlowioSelect/types";
import { useIntl } from "react-intl";
import _pickBy from "lodash/pickBy";
import { createLocalizedOptions } from "src/utils/options";
import {
  TableColumn,
  TableColumnInputType,
  TableColumns,
} from "src/requests/table/types";
import { FormProps, ExportOptions, OnlyCSVExtensionsEnum } from "../types";
import { DATE_FORMAT } from "../constants";

export const createFileNameFromTemplateName = (
  templateName: string
): string => {
  const dateString = format(new Date(), DATE_FORMAT);
  return `${templateName}_${dateString}`;
};

export const getDefaultFormValues = (templateName: string): FormProps => ({
  fileName: createFileNameFromTemplateName(templateName),
  fileExtension: OnlyCSVExtensionsEnum.excel,
  numberOfPagesToExport: 1,
  exportAllPages: false,
});

export const determineHowManyRowsToExport = (
  totalRowsCount: number,
  rowsPerPage: number,
  numberOfPages?: number,
  exportAllPages?: boolean
): number => {
  if (!numberOfPages) return 0;

  if (exportAllPages) return totalRowsCount;

  const rowsToExport = rowsPerPage * numberOfPages;

  if (totalRowsCount < rowsToExport) return totalRowsCount;

  return rowsToExport;
};

export const useGetNumberOfPagesToExportMsg = () => {
  const msgFormatter = useFormatMsg();

  return (numberOfPages: number): string => {
    if (numberOfPages > 1)
      return msgFormatter("table.export_table.number_of_pages", {
        numberOfPages,
      });

    return msgFormatter("table.export_table.only_one_page");
  };
};

export const useValidateNumberOfPages = (numberOfPages: number) => {
  const formatMsg = useFormatMsg();
  return (val: number): boolean | string =>
    (val >= 1 && val <= numberOfPages) ||
    formatMsg("table.export_table.error.number_of_pages");
};

type DetailPageData = {
  templateId: string;
  rowKeyValue: string;
};
export const useDetailPageData = (): DetailPageData | undefined => {
  const { rowKeyValue, templateId } = useParams<DetailPageData>();

  if (!rowKeyValue || !templateId) return undefined;

  return { rowKeyValue, templateId };
};

export const useIsTableItemTemplate = (): boolean => {
  const detailPageData = useDetailPageData();
  return !!detailPageData;
};

export const useTableExportOptions = (numberOfPages: number): Options => {
  const intl = useIntl();
  if (!numberOfPages) {
    return createLocalizedOptions(intl, [
      [ExportOptions.detail, "table.export_table.options.detail"],
    ]);
  }
  return createLocalizedOptions(intl, [
    [ExportOptions.table, "table.export_table.options.table"],
    [ExportOptions.detail, "table.export_table.options.detail"],
    [ExportOptions.tableDetail, "table.export_table.options.table_and_detail"],
  ]);
};

export const isColumnForbiddenType = (column: TableColumn): boolean => {
  const forbiddenTypes = [
    TableColumnInputType.File,
    TableColumnInputType.HegFile,
  ];
  return forbiddenTypes.includes(column.inputType);
};

export const filterHiddenColumn = ([_, column]: [
  string,
  TableColumn
]): boolean => column.inputType !== TableColumnInputType.Hidden;

export const getVisibleColumnIds = (columns: TableColumns): string[] => {
  const visibleColumnEntries =
    Object.entries(columns).filter(filterHiddenColumn);
  return visibleColumnEntries.map(([colKey, _]) => colKey);
};
